// Catalog of every screen the mobile app can render.
// The App Builder UI iterates this to let admins toggle visibility, set
// bottom-nav destinations, choose default columns/actions per list view.
//
// Adding a new mobile screen:
//   1. Add an entry here with a stable id (`<module>.<entity>.<view>`).
//   2. Implement the screen in apps/erp-mobile/app/...
//   3. Wire it into the route resolver in libs/mobile-shell.

import type { ModuleKey } from '@erp/data-access/interfaces';

export type ScreenViewKind =
  | 'dashboard'
  | 'list'
  | 'detail'
  | 'form'
  | 'pos'
  | 'reader' // PDF / receipt / printable preview
  | 'tool';

export interface ScreenMeta {
  /** Stable id: `<module>.<entity>.<view>` — referenced from app_config. */
  id: string;
  /** Module the screen belongs to. Disabled module hides all its screens. */
  module: ModuleKey | 'core' | 'settings';
  /** Translation key under `mobile.screens.<id>.title`. */
  i18nTitleKey: string;
  /** Lucide-react-native icon name. The Builder's icon picker uses this default. */
  defaultIcon: string;
  view: ScreenViewKind;
  /** Can be picked as a bottom-nav destination? (Detail/form views are not.) */
  navigable: boolean;
  /** Optional list of columns the list view can show; admin picks defaults. */
  columns?: string[];
  /** Actions available on items (used to gate per-role). */
  actions?: string[];
  /** Filters available on the list view. */
  filters?: string[];
}

export const SCREEN_CATALOG: readonly ScreenMeta[] = [
  /* ---------------- Core ---------------- */
  { id: 'dashboard', module: 'core', i18nTitleKey: 'screens.dashboard.title',
    defaultIcon: 'home', view: 'dashboard', navigable: true },
  { id: 'search', module: 'core', i18nTitleKey: 'screens.search.title',
    defaultIcon: 'search', view: 'tool', navigable: true },
  { id: 'notifications', module: 'core', i18nTitleKey: 'screens.notifications.title',
    defaultIcon: 'bell', view: 'list', navigable: true },
  { id: 'profile', module: 'core', i18nTitleKey: 'screens.profile.title',
    defaultIcon: 'user', view: 'form', navigable: true },

  /* ---------------- Articles ---------------- */
  { id: 'articles.list', module: 'sales', i18nTitleKey: 'screens.articles.list',
    defaultIcon: 'package', view: 'list', navigable: true,
    columns: ['code', 'name', 'price', 'stock', 'category'],
    actions: ['edit', 'delete', 'duplicate', 'print_label'],
    filters: ['category', 'family', 'brand', 'low_stock', 'archived'] },
  { id: 'articles.detail', module: 'sales', i18nTitleKey: 'screens.articles.detail',
    defaultIcon: 'package', view: 'detail', navigable: false },
  { id: 'articles.create', module: 'sales', i18nTitleKey: 'screens.articles.create',
    defaultIcon: 'plus', view: 'form', navigable: false },
  { id: 'articles.scan', module: 'sales', i18nTitleKey: 'screens.articles.scan',
    defaultIcon: 'scan-barcode', view: 'tool', navigable: true },
  { id: 'articles.categories', module: 'sales', i18nTitleKey: 'screens.articles.categories',
    defaultIcon: 'folder-tree', view: 'list', navigable: false },
  { id: 'articles.families', module: 'sales', i18nTitleKey: 'screens.articles.families',
    defaultIcon: 'layers', view: 'list', navigable: false },
  { id: 'articles.brands', module: 'sales', i18nTitleKey: 'screens.articles.brands',
    defaultIcon: 'tag', view: 'list', navigable: false },

  /* ---------------- Clients & Reps ---------------- */
  { id: 'clients.list', module: 'sales', i18nTitleKey: 'screens.clients.list',
    defaultIcon: 'users', view: 'list', navigable: true,
    columns: ['name', 'phone', 'city', 'balance', 'last_invoice'],
    actions: ['call', 'email', 'whatsapp', 'edit', 'delete', 'view_balance'],
    filters: ['type', 'city', 'with_balance', 'archived'] },
  { id: 'clients.detail', module: 'sales', i18nTitleKey: 'screens.clients.detail',
    defaultIcon: 'user', view: 'detail', navigable: false },
  { id: 'clients.create', module: 'sales', i18nTitleKey: 'screens.clients.create',
    defaultIcon: 'user-plus', view: 'form', navigable: false },
  { id: 'representatives.list', module: 'sales', i18nTitleKey: 'screens.reps.list',
    defaultIcon: 'briefcase', view: 'list', navigable: false },
  { id: 'representatives.detail', module: 'sales', i18nTitleKey: 'screens.reps.detail',
    defaultIcon: 'briefcase', view: 'detail', navigable: false },

  /* ---------------- Suppliers ---------------- */
  { id: 'suppliers.list', module: 'purchases', i18nTitleKey: 'screens.suppliers.list',
    defaultIcon: 'truck', view: 'list', navigable: true,
    columns: ['name', 'phone', 'city', 'balance', 'last_purchase'],
    actions: ['call', 'email', 'whatsapp', 'edit', 'delete'],
    filters: ['city', 'with_balance', 'archived'] },
  { id: 'suppliers.detail', module: 'purchases', i18nTitleKey: 'screens.suppliers.detail',
    defaultIcon: 'truck', view: 'detail', navigable: false },
  { id: 'suppliers.create', module: 'purchases', i18nTitleKey: 'screens.suppliers.create',
    defaultIcon: 'user-plus', view: 'form', navigable: false },

  /* ---------------- Sales ---------------- */
  { id: 'sales.quotes.list', module: 'sales', i18nTitleKey: 'screens.quotes.list',
    defaultIcon: 'file-text', view: 'list', navigable: true,
    columns: ['number', 'client', 'date', 'total', 'invoiced'],
    actions: ['edit', 'duplicate', 'convert_to_order', 'convert_to_delivery', 'pdf', 'share', 'delete'],
    filters: ['period', 'client', 'representative', 'unconverted'] },
  { id: 'sales.quotes.detail', module: 'sales', i18nTitleKey: 'screens.quotes.detail',
    defaultIcon: 'file-text', view: 'detail', navigable: false },
  { id: 'sales.quotes.form', module: 'sales', i18nTitleKey: 'screens.quotes.form',
    defaultIcon: 'file-plus', view: 'form', navigable: false },

  { id: 'sales.orders.list', module: 'sales', i18nTitleKey: 'screens.orders.list',
    defaultIcon: 'clipboard-list', view: 'list', navigable: true,
    columns: ['number', 'client', 'date', 'total', 'delivered'],
    actions: ['edit', 'convert_to_delivery', 'pdf', 'share', 'delete'],
    filters: ['period', 'client', 'undelivered'] },
  { id: 'sales.orders.detail', module: 'sales', i18nTitleKey: 'screens.orders.detail',
    defaultIcon: 'clipboard-list', view: 'detail', navigable: false },
  { id: 'sales.orders.form', module: 'sales', i18nTitleKey: 'screens.orders.form',
    defaultIcon: 'clipboard-list', view: 'form', navigable: false },

  { id: 'sales.deliveries.list', module: 'sales', i18nTitleKey: 'screens.deliveries.list',
    defaultIcon: 'truck', view: 'list', navigable: true,
    columns: ['number', 'client', 'date', 'total', 'invoiced'],
    actions: ['edit', 'convert_to_invoice', 'capture_signature', 'pdf', 'share', 'delete'],
    filters: ['period', 'client', 'uninvoiced'] },
  { id: 'sales.deliveries.detail', module: 'sales', i18nTitleKey: 'screens.deliveries.detail',
    defaultIcon: 'truck', view: 'detail', navigable: false },
  { id: 'sales.deliveries.form', module: 'sales', i18nTitleKey: 'screens.deliveries.form',
    defaultIcon: 'truck', view: 'form', navigable: false },

  { id: 'sales.invoices.list', module: 'sales', i18nTitleKey: 'screens.invoices.list',
    defaultIcon: 'receipt', view: 'list', navigable: true,
    columns: ['number', 'client', 'date', 'total', 'paid', 'due'],
    actions: ['edit', 'pay', 'pdf', 'share', 'duplicate', 'credit_note', 'delete'],
    filters: ['period', 'client', 'unpaid', 'overdue'] },
  { id: 'sales.invoices.detail', module: 'sales', i18nTitleKey: 'screens.invoices.detail',
    defaultIcon: 'receipt', view: 'detail', navigable: false },
  { id: 'sales.invoices.form', module: 'sales', i18nTitleKey: 'screens.invoices.form',
    defaultIcon: 'receipt', view: 'form', navigable: false },
  { id: 'sales.invoices.pay', module: 'sales', i18nTitleKey: 'screens.invoices.pay',
    defaultIcon: 'wallet', view: 'form', navigable: false },

  { id: 'sales.creditNotes.list', module: 'sales', i18nTitleKey: 'screens.creditNotes.list',
    defaultIcon: 'rotate-ccw', view: 'list', navigable: false,
    columns: ['number', 'client', 'date', 'total'],
    actions: ['pdf', 'share', 'delete'],
    filters: ['period', 'client'] },
  { id: 'sales.creditNotes.detail', module: 'sales', i18nTitleKey: 'screens.creditNotes.detail',
    defaultIcon: 'rotate-ccw', view: 'detail', navigable: false },
  { id: 'sales.creditNotes.form', module: 'sales', i18nTitleKey: 'screens.creditNotes.form',
    defaultIcon: 'rotate-ccw', view: 'form', navigable: false },

  /* ---------------- Caisse / POS ---------------- */
  { id: 'caisse.dashboard', module: 'caisse', i18nTitleKey: 'screens.caisse.dashboard',
    defaultIcon: 'monitor', view: 'dashboard', navigable: true },
  { id: 'caisse.pos', module: 'caisse', i18nTitleKey: 'screens.caisse.pos',
    defaultIcon: 'shopping-cart', view: 'pos', navigable: true,
    actions: ['scan', 'discount', 'hold_cart', 'split_payment', 'print_receipt'] },
  { id: 'caisse.sessions.list', module: 'caisse', i18nTitleKey: 'screens.caisse.sessions',
    defaultIcon: 'clock', view: 'list', navigable: false,
    columns: ['register', 'opened_at', 'closed_at', 'sales', 'cash'] },
  { id: 'caisse.sessions.detail', module: 'caisse', i18nTitleKey: 'screens.caisse.sessionDetail',
    defaultIcon: 'clock', view: 'detail', navigable: false },
  { id: 'caisse.registers.list', module: 'caisse', i18nTitleKey: 'screens.caisse.registers',
    defaultIcon: 'printer', view: 'list', navigable: false },

  /* ---------------- Purchases ---------------- */
  { id: 'purchases.orders.list', module: 'purchases', i18nTitleKey: 'screens.purchases.orders.list',
    defaultIcon: 'clipboard-check', view: 'list', navigable: true,
    columns: ['number', 'supplier', 'date', 'total', 'received'],
    actions: ['edit', 'convert_to_reception', 'pdf', 'share', 'delete'],
    filters: ['period', 'supplier', 'pending'] },
  { id: 'purchases.orders.detail', module: 'purchases', i18nTitleKey: 'screens.purchases.orders.detail',
    defaultIcon: 'clipboard-check', view: 'detail', navigable: false },
  { id: 'purchases.orders.form', module: 'purchases', i18nTitleKey: 'screens.purchases.orders.form',
    defaultIcon: 'clipboard-check', view: 'form', navigable: false },

  { id: 'purchases.receptions.list', module: 'purchases', i18nTitleKey: 'screens.purchases.receptions.list',
    defaultIcon: 'package-check', view: 'list', navigable: true,
    columns: ['number', 'supplier', 'date', 'total', 'invoiced'],
    actions: ['edit', 'convert_to_invoice', 'photo_proof', 'pdf', 'delete'],
    filters: ['period', 'supplier', 'uninvoiced'] },
  { id: 'purchases.receptions.detail', module: 'purchases', i18nTitleKey: 'screens.purchases.receptions.detail',
    defaultIcon: 'package-check', view: 'detail', navigable: false },
  { id: 'purchases.receptions.form', module: 'purchases', i18nTitleKey: 'screens.purchases.receptions.form',
    defaultIcon: 'package-check', view: 'form', navigable: false },

  { id: 'purchases.invoices.list', module: 'purchases', i18nTitleKey: 'screens.purchases.invoices.list',
    defaultIcon: 'file-input', view: 'list', navigable: true,
    columns: ['number', 'supplier', 'date', 'total', 'paid'],
    actions: ['edit', 'pay', 'pdf', 'credit_note', 'delete'],
    filters: ['period', 'supplier', 'unpaid'] },
  { id: 'purchases.invoices.detail', module: 'purchases', i18nTitleKey: 'screens.purchases.invoices.detail',
    defaultIcon: 'file-input', view: 'detail', navigable: false },
  { id: 'purchases.invoices.form', module: 'purchases', i18nTitleKey: 'screens.purchases.invoices.form',
    defaultIcon: 'file-input', view: 'form', navigable: false },
  { id: 'purchases.invoices.pay', module: 'purchases', i18nTitleKey: 'screens.purchases.invoices.pay',
    defaultIcon: 'wallet', view: 'form', navigable: false },

  { id: 'purchases.creditNotes.list', module: 'purchases', i18nTitleKey: 'screens.purchases.creditNotes.list',
    defaultIcon: 'rotate-ccw', view: 'list', navigable: false },
  { id: 'purchases.creditNotes.detail', module: 'purchases', i18nTitleKey: 'screens.purchases.creditNotes.detail',
    defaultIcon: 'rotate-ccw', view: 'detail', navigable: false },
  { id: 'purchases.creditNotes.form', module: 'purchases', i18nTitleKey: 'screens.purchases.creditNotes.form',
    defaultIcon: 'rotate-ccw', view: 'form', navigable: false },

  /* ---------------- Stock ---------------- */
  { id: 'stock.movements', module: 'stock', i18nTitleKey: 'screens.stock.movements',
    defaultIcon: 'arrow-left-right', view: 'list', navigable: true,
    columns: ['date', 'article', 'warehouse', 'qty', 'type'],
    actions: ['transfer', 'adjust'],
    filters: ['period', 'article', 'warehouse', 'type'] },
  { id: 'stock.warehouses', module: 'stock', i18nTitleKey: 'screens.stock.warehouses',
    defaultIcon: 'warehouse', view: 'list', navigable: false },
  { id: 'stock.transfer', module: 'stock', i18nTitleKey: 'screens.stock.transfer',
    defaultIcon: 'arrow-left-right', view: 'form', navigable: false },
  { id: 'stock.inventory', module: 'stock', i18nTitleKey: 'screens.stock.inventory',
    defaultIcon: 'clipboard-list', view: 'tool', navigable: true,
    actions: ['scan', 'manual_count', 'submit'] },

  /* ---------------- Payments ---------------- */
  { id: 'payments.list', module: 'payments', i18nTitleKey: 'screens.payments.list',
    defaultIcon: 'banknote', view: 'list', navigable: true,
    columns: ['date', 'party', 'amount', 'method', 'direction'],
    actions: ['edit', 'reconcile', 'delete'],
    filters: ['period', 'party', 'method', 'direction'] },
  { id: 'payments.receive', module: 'payments', i18nTitleKey: 'screens.payments.receive',
    defaultIcon: 'arrow-down-to-line', view: 'form', navigable: false },
  { id: 'payments.send', module: 'payments', i18nTitleKey: 'screens.payments.send',
    defaultIcon: 'arrow-up-from-line', view: 'form', navigable: false },

  /* ---------------- Treasury ---------------- */
  { id: 'treasury.accounts', module: 'treasury', i18nTitleKey: 'screens.treasury.accounts',
    defaultIcon: 'landmark', view: 'list', navigable: true,
    columns: ['bank', 'rib', 'balance', 'currency'] },
  { id: 'treasury.account.detail', module: 'treasury', i18nTitleKey: 'screens.treasury.detail',
    defaultIcon: 'landmark', view: 'detail', navigable: false },

  /* ---------------- Checks ---------------- */
  { id: 'checks.list', module: 'checks', i18nTitleKey: 'screens.checks.list',
    defaultIcon: 'book-check', view: 'list', navigable: true,
    columns: ['number', 'party', 'amount', 'due_date', 'state'],
    actions: ['transition', 'capture_photo', 'edit', 'delete'],
    filters: ['period', 'party', 'state', 'direction'] },
  { id: 'traites.list', module: 'checks', i18nTitleKey: 'screens.traites.list',
    defaultIcon: 'file-check', view: 'list', navigable: true,
    columns: ['number', 'party', 'amount', 'due_date', 'state'],
    actions: ['transition', 'capture_photo', 'edit', 'delete'],
    filters: ['period', 'party', 'state'] },

  /* ---------------- Withholding (RAS) ---------------- */
  { id: 'withholding.sales.list', module: 'withholding', i18nTitleKey: 'screens.withholding.sales.list',
    defaultIcon: 'file-text', view: 'list', navigable: true,
    columns: ['number', 'client', 'date', 'amount'] },
  { id: 'withholding.sales.detail', module: 'withholding', i18nTitleKey: 'screens.withholding.sales.detail',
    defaultIcon: 'file-text', view: 'detail', navigable: false },
  { id: 'withholding.sales.declarations', module: 'withholding', i18nTitleKey: 'screens.withholding.sales.declarations',
    defaultIcon: 'folder', view: 'list', navigable: false,
    actions: ['generate_tej', 'download_zip'] },
  { id: 'withholding.purchases.list', module: 'withholding', i18nTitleKey: 'screens.withholding.purchases.list',
    defaultIcon: 'file-text', view: 'list', navigable: true,
    columns: ['number', 'supplier', 'date', 'amount'] },
  { id: 'withholding.purchases.detail', module: 'withholding', i18nTitleKey: 'screens.withholding.purchases.detail',
    defaultIcon: 'file-text', view: 'detail', navigable: false },
  { id: 'withholding.purchases.declarations', module: 'withholding', i18nTitleKey: 'screens.withholding.purchases.declarations',
    defaultIcon: 'folder', view: 'list', navigable: false,
    actions: ['generate_tej', 'download_zip'] },

  /* ---------------- Storefront management ---------------- */
  { id: 'storefront.dashboard', module: 'storefront', i18nTitleKey: 'screens.storefront.dashboard',
    defaultIcon: 'globe', view: 'dashboard', navigable: true },
  { id: 'storefront.orders', module: 'storefront', i18nTitleKey: 'screens.storefront.orders',
    defaultIcon: 'shopping-bag', view: 'list', navigable: true,
    columns: ['number', 'customer', 'date', 'total', 'status'],
    actions: ['update_status', 'create_shipment', 'view'],
    filters: ['period', 'status'] },
  { id: 'storefront.order.detail', module: 'storefront', i18nTitleKey: 'screens.storefront.orderDetail',
    defaultIcon: 'shopping-bag', view: 'detail', navigable: false },
  { id: 'storefront.products', module: 'storefront', i18nTitleKey: 'screens.storefront.products',
    defaultIcon: 'box', view: 'list', navigable: false,
    columns: ['name', 'price', 'stock', 'status'],
    actions: ['edit', 'publish', 'unpublish', 'delete'] },
  { id: 'storefront.product.detail', module: 'storefront', i18nTitleKey: 'screens.storefront.productDetail',
    defaultIcon: 'box', view: 'form', navigable: false },
  { id: 'storefront.customers', module: 'storefront', i18nTitleKey: 'screens.storefront.customers',
    defaultIcon: 'users', view: 'list', navigable: false },
  { id: 'storefront.abandonedCarts', module: 'storefront', i18nTitleKey: 'screens.storefront.abandonedCarts',
    defaultIcon: 'shopping-cart', view: 'list', navigable: false,
    actions: ['recover'] },
  { id: 'storefront.analytics', module: 'storefront', i18nTitleKey: 'screens.storefront.analytics',
    defaultIcon: 'bar-chart-3', view: 'dashboard', navigable: true },
  { id: 'storefront.notifications', module: 'storefront', i18nTitleKey: 'screens.storefront.notifications',
    defaultIcon: 'bell', view: 'list', navigable: false },

  /* ---------------- Settings ---------------- */
  { id: 'settings.hub', module: 'settings', i18nTitleKey: 'screens.settings.hub',
    defaultIcon: 'settings', view: 'dashboard', navigable: true },
  { id: 'settings.company', module: 'settings', i18nTitleKey: 'screens.settings.company',
    defaultIcon: 'building', view: 'form', navigable: false },
  { id: 'settings.appBuilder', module: 'settings', i18nTitleKey: 'screens.settings.appBuilder',
    defaultIcon: 'sliders-horizontal', view: 'form', navigable: true },
  { id: 'settings.theme', module: 'settings', i18nTitleKey: 'screens.settings.theme',
    defaultIcon: 'palette', view: 'form', navigable: false },
  { id: 'settings.language', module: 'settings', i18nTitleKey: 'screens.settings.language',
    defaultIcon: 'languages', view: 'form', navigable: false },
];

export type ScreenId = typeof SCREEN_CATALOG[number]['id'];

export function getScreen(id: string): ScreenMeta | undefined {
  return SCREEN_CATALOG.find((s) => s.id === id);
}

export function screensForModule(module: ModuleKey | 'core' | 'settings'): ScreenMeta[] {
  return SCREEN_CATALOG.filter((s) => s.module === module);
}

export function navigableScreens(): ScreenMeta[] {
  return SCREEN_CATALOG.filter((s) => s.navigable);
}
